/**
 * Offline Cache Service using IndexedDB
 *
 * Provides persistent offline storage for:
 * - Activity codes (for field personnel to work offline)
 * - Daily report submissions (queued when offline, synced when online)
 * - Equipment inventory (units, panels, pressure equipment, consumables)
 * - Operation and activity data
 *
 * Supports 4+ days of offline operation as required.
 */

import * as Types from '@/types'

const DB_NAME = 'expro_offline_db'
const DB_VERSION = 1

// Store names
const STORES = {
  ACTIVITY_CODES: 'activity_codes',
  ACTIVITY_TEMPLATES: 'activity_templates',
  EQUIPMENT_UNITS: 'equipment_units',
  EQUIPMENT_PANELS: 'equipment_panels',
  EQUIPMENT_PRESSURE: 'equipment_pressure_equipment',
  EQUIPMENT_CONSUMABLES: 'equipment_consumables',
  DAILY_REPORTS: 'daily_reports',
  OPERATIONS: 'operations',
  SUBMISSION_QUEUE: 'submission_queue',
  SYNC_METADATA: 'sync_metadata',
}

export interface CachedDailyReport extends Types.OperationDailyReport {
  __synced?: boolean
  __syncError?: string
}

export interface QueuedSubmission {
  id: string
  type: 'daily_report' | 'activity' | 'equipment'
  operationId: string
  reportId?: string
  data: any
  createdAt: number
  retries: number
  lastError?: string
}

export interface SyncMetadata {
  key: string
  lastSyncTime: number
  recordCount: number
}

export class OfflineCache {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void>

  constructor() {
    this.initPromise = this.initializeDatabase()
  }

  /**
   * Initialize IndexedDB database with all required stores
   */
  private async initializeDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error)
        reject(new Error('Failed to initialize offline cache'))
      }

      request.onsuccess = () => {
        this.db = request.result
        console.log('✅ Offline cache initialized')
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Activity codes store
        if (!db.objectStoreNames.contains(STORES.ACTIVITY_CODES)) {
          const store = db.createObjectStore(STORES.ACTIVITY_CODES, { keyPath: 'id' })
          store.createIndex('iadc_code', 'iadc_code', { unique: false })
          store.createIndex('major_category', 'major_category', { unique: false })
          store.createIndex('is_active', 'is_active', { unique: false })
        }

        // Activity templates store
        if (!db.objectStoreNames.contains(STORES.ACTIVITY_TEMPLATES)) {
          db.createObjectStore(STORES.ACTIVITY_TEMPLATES, { keyPath: 'id' })
        }

        // Equipment stores
        if (!db.objectStoreNames.contains(STORES.EQUIPMENT_UNITS)) {
          db.createObjectStore(STORES.EQUIPMENT_UNITS, { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains(STORES.EQUIPMENT_PANELS)) {
          db.createObjectStore(STORES.EQUIPMENT_PANELS, { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains(STORES.EQUIPMENT_PRESSURE)) {
          db.createObjectStore(STORES.EQUIPMENT_PRESSURE, { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains(STORES.EQUIPMENT_CONSUMABLES)) {
          db.createObjectStore(STORES.EQUIPMENT_CONSUMABLES, { keyPath: 'id' })
        }

        // Daily reports store
        if (!db.objectStoreNames.contains(STORES.DAILY_REPORTS)) {
          const store = db.createObjectStore(STORES.DAILY_REPORTS, { keyPath: 'id' })
          store.createIndex('operation_id', 'operation_id', { unique: false })
          store.createIndex('date', 'date', { unique: false })
          store.createIndex('__synced', '__synced', { unique: false })
        }

        // Operations store
        if (!db.objectStoreNames.contains(STORES.OPERATIONS)) {
          db.createObjectStore(STORES.OPERATIONS, { keyPath: 'id' })
        }

        // Submission queue store
        if (!db.objectStoreNames.contains(STORES.SUBMISSION_QUEUE)) {
          const store = db.createObjectStore(STORES.SUBMISSION_QUEUE, { keyPath: 'id' })
          store.createIndex('type', 'type', { unique: false })
          store.createIndex('createdAt', 'createdAt', { unique: false })
        }

        // Sync metadata store
        if (!db.objectStoreNames.contains(STORES.SYNC_METADATA)) {
          db.createObjectStore(STORES.SYNC_METADATA, { keyPath: 'key' })
        }

        console.log('✅ Database schema created')
      }
    })
  }

  /**
   * Ensure database is initialized before operations
   */
  private async ensureInitialized(): Promise<IDBDatabase> {
    await this.initPromise
    if (!this.db) {
      throw new Error('Database initialization failed')
    }
    return this.db
  }

  // ============================================================================
  // Activity Codes Caching
  // ============================================================================

  /**
   * Cache activity codes for offline use
   */
  async cacheActivityCodes(codes: Types.ActivityCode[]): Promise<void> {
    const db = await this.ensureInitialized()
    const tx = db.transaction([STORES.ACTIVITY_CODES, STORES.SYNC_METADATA], 'readwrite')

    try {
      const codesStore = tx.objectStore(STORES.ACTIVITY_CODES)
      const metadataStore = tx.objectStore(STORES.SYNC_METADATA)

      // Clear existing codes
      await this.clearStore(codesStore)

      // Add all codes
      for (const code of codes) {
        await new Promise<void>((resolve, reject) => {
          const req = codesStore.add(code)
          req.onsuccess = () => resolve()
          req.onerror = () => reject(req.error)
        })
      }

      // Update sync metadata
      const metadata: SyncMetadata = {
        key: 'activity_codes',
        lastSyncTime: Date.now(),
        recordCount: codes.length,
      }
      await new Promise<void>((resolve, reject) => {
        const req = metadataStore.put(metadata)
        req.onsuccess = () => resolve()
        req.onerror = () => reject(req.error)
      })

      console.log(`✅ Cached ${codes.length} activity codes`)
    } catch (error) {
      console.error('Failed to cache activity codes:', error)
      throw error
    }
  }

  /**
   * Get cached activity codes
   */
  async getCachedActivityCodes(): Promise<Types.ActivityCode[]> {
    const db = await this.ensureInitialized()
    const tx = db.transaction([STORES.ACTIVITY_CODES], 'readonly')
    const store = tx.objectStore(STORES.ACTIVITY_CODES)

    return new Promise((resolve, reject) => {
      const req = store.getAll()
      req.onsuccess = () => {
        resolve(req.result || [])
      }
      req.onerror = () => {
        reject(req.error)
      }
    })
  }

  /**
   * Get active cached activity codes
   */
  async getActiveCachedActivityCodes(): Promise<Types.ActivityCode[]> {
    const codes = await this.getCachedActivityCodes()
    return codes.filter((code) => code.is_active !== false)
  }

  /**
   * Search cached activity codes
   */
  async searchActivityCodes(query: string): Promise<Types.ActivityCode[]> {
    const codes = await this.getCachedActivityCodes()
    const lowerQuery = query.toLowerCase()

    return codes.filter(
      (code) =>
        code.code?.toLowerCase().includes(lowerQuery) ||
        code.display_name?.toLowerCase().includes(lowerQuery) ||
        code.description?.toLowerCase().includes(lowerQuery) ||
        code.iadc_code?.toLowerCase().includes(lowerQuery) ||
        code.major_category?.toLowerCase().includes(lowerQuery) ||
        code.main_category?.toLowerCase().includes(lowerQuery)
    )
  }

  // ============================================================================
  // Equipment Caching
  // ============================================================================

  /**
   * Cache equipment inventory
   */
  async cacheEquipment(equipment: {
    units: Types.Unit[]
    panels: Types.Panel[]
    pressureEquipment: Types.PressureEquipment[]
    consumables: Types.Consumable[]
  }): Promise<void> {
    const db = await this.ensureInitialized()
    const stores = [
      STORES.EQUIPMENT_UNITS,
      STORES.EQUIPMENT_PANELS,
      STORES.EQUIPMENT_PRESSURE,
      STORES.EQUIPMENT_CONSUMABLES,
      STORES.SYNC_METADATA,
    ]
    const tx = db.transaction(stores, 'readwrite')

    try {
      // Cache units
      const unitsStore = tx.objectStore(STORES.EQUIPMENT_UNITS)
      await this.clearStore(unitsStore)
      for (const unit of equipment.units) {
        await this.addToStore(unitsStore, unit)
      }

      // Cache panels
      const panelsStore = tx.objectStore(STORES.EQUIPMENT_PANELS)
      await this.clearStore(panelsStore)
      for (const panel of equipment.panels) {
        await this.addToStore(panelsStore, panel)
      }

      // Cache pressure equipment
      const pressureStore = tx.objectStore(STORES.EQUIPMENT_PRESSURE)
      await this.clearStore(pressureStore)
      for (const pe of equipment.pressureEquipment) {
        await this.addToStore(pressureStore, pe)
      }

      // Cache consumables
      const consumablesStore = tx.objectStore(STORES.EQUIPMENT_CONSUMABLES)
      await this.clearStore(consumablesStore)
      for (const consumable of equipment.consumables) {
        await this.addToStore(consumablesStore, consumable)
      }

      console.log('✅ Cached equipment inventory')
    } catch (error) {
      console.error('Failed to cache equipment:', error)
      throw error
    }
  }

  /**
   * Get cached equipment
   */
  async getCachedEquipment(): Promise<{
    units: Types.Unit[]
    panels: Types.Panel[]
    pressureEquipment: Types.PressureEquipment[]
    consumables: Types.Consumable[]
  }> {
    const db = await this.ensureInitialized()

    const getStoreData = (storeName: string): Promise<any[]> => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction([storeName], 'readonly')
        const store = tx.objectStore(storeName)
        const req = store.getAll()
        req.onsuccess = () => resolve(req.result || [])
        req.onerror = () => reject(req.error)
      })
    }

    const [units, panels, pressureEquipment, consumables] = await Promise.all([
      getStoreData(STORES.EQUIPMENT_UNITS),
      getStoreData(STORES.EQUIPMENT_PANELS),
      getStoreData(STORES.EQUIPMENT_PRESSURE),
      getStoreData(STORES.EQUIPMENT_CONSUMABLES),
    ])

    return { units, panels, pressureEquipment, consumables }
  }

  // ============================================================================
  // Daily Reports & Submission Queue
  // ============================================================================

  /**
   * Queue a daily report for submission
   */
  async queueDailyReportSubmission(
    operationId: string,
    reportId: string,
    reportData: Types.OperationDailyReport
  ): Promise<void> {
    const db = await this.ensureInitialized()
    const tx = db.transaction(
      [STORES.DAILY_REPORTS, STORES.SUBMISSION_QUEUE],
      'readwrite'
    )

    try {
      // Store the report locally
      const reportsStore = tx.objectStore(STORES.DAILY_REPORTS)
      const cachedReport: CachedDailyReport = {
        ...reportData,
        __synced: false,
      }
      await this.addToStore(reportsStore, cachedReport)

      // Add to submission queue
      const queueStore = tx.objectStore(STORES.SUBMISSION_QUEUE)
      const submission: QueuedSubmission = {
        id: `${reportId}-${Date.now()}`,
        type: 'daily_report',
        operationId,
        reportId,
        data: reportData,
        createdAt: Date.now(),
        retries: 0,
      }
      await this.addToStore(queueStore, submission)

      console.log(`✅ Queued daily report ${reportId} for submission`)
    } catch (error) {
      console.error('Failed to queue daily report:', error)
      throw error
    }
  }

  /**
   * Get all queued submissions
   */
  async getQueuedSubmissions(): Promise<QueuedSubmission[]> {
    const db = await this.ensureInitialized()
    const tx = db.transaction([STORES.SUBMISSION_QUEUE], 'readonly')
    const store = tx.objectStore(STORES.SUBMISSION_QUEUE)

    return new Promise((resolve, reject) => {
      const req = store.getAll()
      req.onsuccess = () => resolve(req.result || [])
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Remove submission from queue (after successful sync)
   */
  async removeQueuedSubmission(submissionId: string): Promise<void> {
    const db = await this.ensureInitialized()
    const tx = db.transaction([STORES.SUBMISSION_QUEUE], 'readwrite')
    const store = tx.objectStore(STORES.SUBMISSION_QUEUE)

    return new Promise((resolve, reject) => {
      const req = store.delete(submissionId)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Update submission with retry info
   */
  async updateQueuedSubmission(
    submissionId: string,
    updates: Partial<QueuedSubmission>
  ): Promise<void> {
    const db = await this.ensureInitialized()
    const tx = db.transaction([STORES.SUBMISSION_QUEUE], 'readwrite')
    const store = tx.objectStore(STORES.SUBMISSION_QUEUE)

    return new Promise(async (resolve, reject) => {
      const getReq = store.get(submissionId)
      getReq.onsuccess = () => {
        const submission = getReq.result
        if (submission) {
          const updated = { ...submission, ...updates }
          const putReq = store.put(updated)
          putReq.onsuccess = () => resolve()
          putReq.onerror = () => reject(putReq.error)
        } else {
          reject(new Error('Submission not found'))
        }
      }
      getReq.onerror = () => reject(getReq.error)
    })
  }

  // ============================================================================
  // Sync Metadata
  // ============================================================================

  /**
   * Get last sync time for a resource
   */
  async getLastSyncTime(resource: string): Promise<number | null> {
    const db = await this.ensureInitialized()
    const tx = db.transaction([STORES.SYNC_METADATA], 'readonly')
    const store = tx.objectStore(STORES.SYNC_METADATA)

    return new Promise((resolve, reject) => {
      const req = store.get(resource)
      req.onsuccess = () => {
        const metadata = req.result as SyncMetadata | undefined
        resolve(metadata?.lastSyncTime || null)
      }
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Check if sync is needed (older than 1 hour)
   */
  async isSyncNeeded(resource: string, maxAge: number = 3600000): Promise<boolean> {
    const lastSync = await this.getLastSyncTime(resource)
    if (!lastSync) return true
    return Date.now() - lastSync > maxAge
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  private clearStore(store: IDBObjectStore): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = store.clear()
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  }

  private addToStore(store: IDBObjectStore, value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const req = store.add(value)
      req.onsuccess = () => resolve()
      req.onerror = () => reject(req.error)
    })
  }

  /**
   * Clear all offline cache data
   */
  async clearAll(): Promise<void> {
    const db = await this.ensureInitialized()
    const storeNames = Object.values(STORES)
    const tx = db.transaction(storeNames, 'readwrite')

    for (const storeName of storeNames) {
      await this.clearStore(tx.objectStore(storeName))
    }

    console.log('✅ Cleared all offline cache')
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<Record<string, number>> {
    const db = await this.ensureInitialized()
    const stats: Record<string, number> = {}

    for (const [key, storeName] of Object.entries(STORES)) {
      try {
        const tx = db.transaction([storeName], 'readonly')
        const store = tx.objectStore(storeName)
        stats[key] = await new Promise((resolve, reject) => {
          const req = store.count()
          req.onsuccess = () => resolve(req.result)
          req.onerror = () => reject(req.error)
        })
      } catch (error) {
        stats[key] = 0
      }
    }

    return stats
  }
}

// Export singleton instance
export const offlineCache = new OfflineCache()
